import type { PayloadAction } from '@reduxjs/toolkit';
import { call, put, type StrictEffect,takeLatest } from 'redux-saga/effects';

import { feedActions } from '@/entities/activity';
import { env } from '@/shared/config/env';
import { type AppLanguage,i18n, SUPPORTED_LANGUAGES } from '@/shared/config/i18n';
import { twa } from '@/shared/lib/telegram/twa';
import { toastService } from '@/shared/lib/toast/toastService';

import { userApi } from '../api/userApi';
import { userActions } from './slice';
import type { User } from './types';

function* ensureAuthToken(): Generator<StrictEffect, void, unknown> {
  const token = localStorage.getItem('token');
  if (token) return;

  if (env.ENVIRONMENT === 'development') {
    // Dev mode — auto-login without Telegram
    const tokens = (yield call(userApi.devLogin)) as {
      access_token: string;
      refresh_token: string;
    };
    localStorage.setItem('token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
  } else if (twa.isInsideTelegram()) {
    // Production — login via Telegram initData
    const initData = twa.getInitData();
    if (initData) {
      // Extract referral code from deep link start param (format: ref_CODE)
      const startParam = twa.getStartParam();
      let referralCode: string | undefined;
      if (startParam && startParam.startsWith('ref_')) {
        referralCode = startParam.slice(4);
      }

      const tokens = (yield call(userApi.telegramLogin, initData, referralCode)) as {
        access_token: string;
        refresh_token: string;
      };
      localStorage.setItem('token', tokens.access_token);
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
  }
}

function* fetchUserSaga(): Generator<StrictEffect, void, unknown> {
  try {
    yield call(ensureAuthToken);
    const user = (yield call(userApi.fetchUser)) as User;
    yield put(userActions.fetchUserSuccess(user));
    const lang = user.language;
    if (SUPPORTED_LANGUAGES.includes(lang) && i18n.language !== lang) {
      try {
        yield call([i18n, i18n.changeLanguage], lang);
        localStorage.setItem('language', lang);
      } catch {
        // i18n sync failure is non-fatal — app stays functional in current language
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch user';
    yield put(userActions.fetchUserFailure(errorMessage));
  }
}

function* updateLanguageSaga(action: PayloadAction<AppLanguage>): Generator<StrictEffect, void, unknown> {
  yield put(userActions.updateLanguageSuccess(action.payload));
  try {
    yield call(userApi.updateLanguage, action.payload);
  } catch {
    toastService.error(i18n.t('common.languageSaveError'));
  }
}

function* initAuthSaga(): Generator<StrictEffect, void, unknown> {
  yield call(ensureAuthToken);
  yield call(fetchUserSaga);
  yield put(feedActions.fetchFeedRequest());
}

export function* userWatcher(): Generator<StrictEffect, void, unknown> {
  yield call(initAuthSaga);
  yield takeLatest(userActions.fetchUserRequest.type, fetchUserSaga);
  yield takeLatest(userActions.updateLanguageRequest.type, updateLanguageSaga);
}
